<template>
  <div
      class="orm-login-widget text-left"
      role="dialog"
      aria-modal="true"
      aria-labelledby="loginHeadline"
      v-click-outside="handleClick"
  >
    <!-- Dynamische Überschrift mit eindeutiger ID -->
    <p
        :id="'loginHeadline'"
        class="orm-login-headline"
    >
      {{ forgotpw ? i18n("customer/login/forgott_pw") : i18n("customer/login/title") }}
    </p>

    <!-- E-Mail-Feld -->
    <FormInput
        ref="emailInput"
        v-if="!sendSuccess && !requestLoading && !showSuccess"
        v-model="form.email"
        type="text"
        my-id="email"
        :my-label="i18n('form/label/email')"
        :is-required="true"
        :error="validate.email"
        aria-required="true"
    />

    <!-- Passwort-Feld -->
    <FormPwd
        v-if="!forgotpw && !requestLoading && !showSuccess"
        v-model="form.password"
        type="text"
        my-id="password"
        :my-label="i18n('form/label/password') || 'Passwort'"
        :is-required="true"
        :error="validate.password"
        aria-required="true"
    />

    <!-- Fehlermeldungen -->
    <div
        v-if="errors.length"
        class="orm-login-error"
        role="alert"
        aria-live="assertive"
    >
      <small v-for="error in errors" :key="error">{{ error }}</small>
    </div>

    <!-- Erfolgs-Feedback nach "Passwort vergessen" -->
    <div v-if="sendSuccess" aria-live="polite">
      {{ i18n("customer/login/mail_success") }}
      <div
          @click="goBackToLogin"
          class="orm-btn orm-login-widget-btnlistener"
          role="button"
          tabindex="0"
      >
        {{ i18n("customer/login/back_to_login") }}
      </div>
    </div>

    <!-- Passwort vergessen Link -->
    <div class="orm-pwdlink">
      <p
          class="orm-login-widget-btnlistener"
          v-if="!forgotpw && !requestLoading"
          @click="forgotpassword"
          @keydown.enter="forgotpassword"
          role="link"
          tabindex="0"
      >
        {{ i18n("customer/login/forgott_pw") }}?
      </p>
    </div>

    <!-- Anmelden Button -->
    <button
        class="orm-btn orm-login-widget-btnlistener"
        v-if="!forgotpw && !requestLoading"
        @click="authenticate"
    >
      {{ i18n("customer/login/button_submit") }}
    </button>

    <!-- Passwort zurücksetzen Button -->
    <button
        class="orm-btn orm-login-widget-btnlistener"
        v-if="forgotpw && !sendSuccess && !requestLoading && !showSuccess"
        @click="requestPassword"
    >
      {{ i18n("customer/login/reset_pw") }}
    </button>

    <!-- Ladeanzeige -->
    <div v-if="requestLoading" class="text-center" aria-live="polite">
      <div class="orm-loader-spinner">
        <i class="fa fa-spinner-third orm-rotate360" aria-hidden="true"></i>
      </div>
      <p v-if="forgotpw">Anfrage wird bearbeitet...</p>
    </div>

    <!-- Erfolgsanzeige nach Passwort-Anfrage -->
    <div v-if="showSuccess" class="text-center" aria-live="polite">
      <p>E-Mail mit Link zum Passwort zurücksetzen wurde verschickt.</p>
      <button
          class="orm-btn orm-login-widget-btnlistener"
          @click="goBackToLogin"
          @keydown.enter="goBackToLogin"
      >
        Zurück zum Login
      </button>
    </div>
  </div>
</template>

<script>
import store from "@/store";
import i18n from "@/mixins/mixins";
import FormInput from "@/components/ui/INPUTS/FormInput.vue";
import FormPwd from "@/components/ui/INPUTS/FormPwd.vue";

export default {
  emits: ["getLogin", "showCustomerOverlay"],
  mixins: [i18n],
  components: { FormInput, FormPwd },
  props: {
    showLogo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        email:
            process.env.NODE_ENV === "development" ||
            process.env.VUE_APP_DEMO === "true"
                ? "webmodul@cx9.de"
                : "",
        password:
            process.env.NODE_ENV === "development" ||
            process.env.VUE_APP_DEMO === "true"
                ? "gNSISkP8"
                : "",
      },
      validate: {
        email: false,
        password: false,
      },
      forgotpw: false,
      sendSuccess: false,
      errors: [],
      requestLoading: false,
      showSuccess: false,
      logo: null,
      logoheight: process.env.VUE_APP_CUSTOMER_LOGO_HEIGHT_RESERVATION,
      env: process.env,
    };
  },
  mounted() {
    const clientdata = store.getters["general/GET_CLIENT_DATA"];
    if (clientdata["reservationheader-logo"]) {
      this.logo = clientdata["reservationheader-logo"].includes("http")
          ? clientdata["reservationheader-logo"]
          : process.env.VUE_APP_BASE_URL + clientdata["reservationheader-logo"];
    }

    document.addEventListener("keydown", this.handleEscape);
    this.focusEmail();
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscape);
  },
  methods: {
    handleEscape(e) {
      if (e.key === "Escape") {
        this.$emit("showCustomerOverlay", false);
      }
    },
    focusEmail() {
      this.$nextTick(() => {
        this.$refs.emailInput?.$el?.querySelector("input")?.focus();
      });
    },
    validateForm() {
      this.errors = [];
      for (const formfield in this.validate) {
        if (Object.hasOwnProperty.call(this.validate, formfield)) {
          const element = this.form[formfield];
          if (formfield === "email") {
            this.validate[formfield] = !this.validEmail(element);
          } else {
            this.validate[formfield] = !element;
          }
        }
      }
      return this.errors.length === 0;
    },
    validEmail(email) {
      const re =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },
    authenticate() {
      if (!this.validateForm()) return;

      this.requestLoading = true;
      store
          .dispatch("customer/AUTHENTICATE", {
            email: this.form.email,
            password: this.form.password,
          })
          .then((result) => {
            if (result === true) {
              store.dispatch("customer/GET_USER_DATA").then(() => {
                this.$emit("getLogin", true);
                location.reload();
              });
            } else {
              this.requestLoading = false;
              this.validate.email = true;
              this.validate.password = true;
              const message =
                  this.i18n("customer/login/error_login") ||
                  "Emailadresse oder Passwort falsch.";
              this.errors.push(message);
            }
          });
    },
    forgotpassword() {
      this.validate.email = false;
      this.validate.password = false;
      this.errors = [];
      this.forgotpw = true;
      this.$nextTick(() => this.focusEmail());
    },
    requestPassword() {
      this.requestLoading = true;
      store
          .dispatch("customer/FORGOTPASSWORD", {
            email: this.form.email,
            return_url: process.env.VUE_APP_CUSTOMER_PATH + "forgot/",
          })
          .then((result) => {
            if (result) {
              this.showSuccess = true;
              this.requestLoading = false;
            } else {
              if (process.env.VUE_APP_STANDALONE === "true") {
                this.$router.push({name: "Home"});
              } else {
                window.location.href = process.env.VUE_APP_BASE_URL;
              }
            }
          })
          .catch((err) => {
            console.log(err);
          });
    },
    goBackToLogin() {
      this.forgotpw = false;
      this.sendSuccess = false;
      this.showSuccess = false;
      this.$nextTick(() => this.focusEmail());
    },
    handleClick(ev) {
      if (
          !ev.target.classList.contains("orm-login-widget-btnlistener") &&
          !ev.target.classList.contains("fa-user-circle")
      ) {
        this.$emit("showCustomerOverlay", false);
      }
    },
  },
};
</script>
